import React, { useState } from "react";
import { useForm } from "react-hook-form";
import Card from "../Card/Card";
import { Col, ControlLabel, Form, FormGroup, HelpBlock, Modal, Row } from "react-bootstrap";
import Button from "../CustomButton/CustomButton";
import BeatLoader from "react-spinners/BeatLoader";
import SettingsInput from "../Settings/SettingsInput";

const DESCRIPTION_MAX_LENGTH = 500;
// A full day, so an all-day service is expressible without opening the door to
// durations that would never fit inside a booking day.
const MAX_DURATION_MINUTES = 1440;

// Add / edit modal for an appointment service (`businesses/{id}/services`).
// Field coercion and the omit-blank-optionals rule live in
// helpers/appointmentService.helper.js, applied by the service layer on write.
const AddService = ({ showServiceModal, closeModal, service, updateService, loading }) => {
  const { register, handleSubmit, errors } = useForm();

  const [form, setForm] = useState({
    name: service?.name || "",
    duration: service?.duration || 30,
    price: service?.price ?? "",
    description: service?.description || "",
    bufferBefore: service?.bufferBefore ?? "",
    bufferAfter: service?.bufferAfter ?? "",
    active: service ? service.active !== false : true
  });

  const onSubmit = data => {
    updateService({ ...data, active: form.active });
  };

  const toggleChecked = event => {
    setForm({ ...form, [event.target.name]: event.target.checked });
  };

  const handleTextareaChange = key => event => {
    setForm({ ...form, [key]: event.target.value });
  };

  return (
    <Modal enforceFocus={false} dialogClassName="add-guest-modal" show={showServiceModal} onHide={closeModal}>
      <Card
        content={
          <Form noValidate className="edit-user" onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col className="edit-user__title" md={12}>
                {`${service?.id ? "Edit" : "Add"} Service`}
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup validationState={(!!errors.name && "error") || null}>
                  <ControlLabel className="edit-user-label">NAME</ControlLabel>
                  <input
                    name="name"
                    className="form-control"
                    type="text"
                    disabled={loading}
                    placeholder="e.g. Haircut"
                    defaultValue={form.name}
                    ref={register({ required: true, maxLength: 60 })}
                  />
                  {errors.name && errors.name.type === "required" && <HelpBlock>Name is required</HelpBlock>}
                  {errors.name && errors.name.type === "maxLength" && (
                    <HelpBlock>Name should be less than 60 characters</HelpBlock>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup validationState={(!!errors.duration && "error") || null}>
                  <ControlLabel className="edit-user-label">DURATION (MINUTES)</ControlLabel>
                  <input
                    name="duration"
                    className="form-control"
                    type="number"
                    disabled={loading}
                    placeholder="e.g. 45"
                    defaultValue={form.duration}
                    ref={register({ required: true, min: 1, max: MAX_DURATION_MINUTES })}
                  />
                  {errors.duration && errors.duration.type === "required" && (
                    <HelpBlock>Duration is required</HelpBlock>
                  )}
                  {errors.duration && errors.duration.type === "min" && (
                    <HelpBlock>Duration must be at least 1 minute</HelpBlock>
                  )}
                  {errors.duration && errors.duration.type === "max" && (
                    <HelpBlock>Duration should be less than {MAX_DURATION_MINUTES} minutes</HelpBlock>
                  )}
                  <HelpBlock style={{ color: "#626262" }}>
                    How long the appointment takes. Every resource performing this service uses this duration.
                  </HelpBlock>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup validationState={(!!errors.price && "error") || null}>
                  <ControlLabel className="edit-user-label">PRICE (OPTIONAL)</ControlLabel>
                  <input
                    name="price"
                    className="form-control"
                    type="number"
                    step="0.01"
                    disabled={loading}
                    placeholder="Leave blank to hide the price"
                    defaultValue={form.price}
                    ref={register({ min: 0, max: 100000 })}
                  />
                  {errors.price && errors.price.type === "min" && <HelpBlock>Price cannot be negative</HelpBlock>}
                  {errors.price && errors.price.type === "max" && <HelpBlock>Price is too large</HelpBlock>}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup validationState={(!!errors.bufferBefore && "error") || null}>
                  <ControlLabel className="edit-user-label">BUFFER BEFORE (MIN)</ControlLabel>
                  <input
                    name="bufferBefore"
                    className="form-control"
                    type="number"
                    disabled={loading}
                    placeholder="0"
                    defaultValue={form.bufferBefore}
                    ref={register({ min: 0, max: 240 })}
                  />
                  {errors.bufferBefore && <HelpBlock>Buffer must be between 0 and 240 minutes</HelpBlock>}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup validationState={(!!errors.bufferAfter && "error") || null}>
                  <ControlLabel className="edit-user-label">BUFFER AFTER (MIN)</ControlLabel>
                  <input
                    name="bufferAfter"
                    className="form-control"
                    type="number"
                    disabled={loading}
                    placeholder="0"
                    defaultValue={form.bufferAfter}
                    ref={register({ min: 0, max: 240 })}
                  />
                  {errors.bufferAfter && <HelpBlock>Buffer must be between 0 and 240 minutes</HelpBlock>}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <ControlLabel className="edit-user-label">DESCRIPTION (OPTIONAL)</ControlLabel>
                  <textarea
                    name="description"
                    className="form-control"
                    disabled={loading}
                    maxLength={DESCRIPTION_MAX_LENGTH}
                    style={{ resize: "none", height: 80 }}
                    placeholder="Shown to guests alongside the service name"
                    value={form.description}
                    onChange={handleTextareaChange("description")}
                    ref={register()}
                  />
                  <div style={{ marginTop: 5, fontSize: 12, color: "#626262" }}>
                    {form.description.length}/{DESCRIPTION_MAX_LENGTH} characters
                  </div>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup style={{ margin: 0 }}>
                  <ControlLabel style={{ float: "left", marginTop: 17, fontSize: 14 }}>BOOKABLE</ControlLabel>
                  <SettingsInput
                    form={form}
                    name={"active"}
                    type={"switch"}
                    enabled={true}
                    style={{ marginBottom: 0 }}
                    value={form.active}
                    register={null}
                    onChangeValue={toggleChecked}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className="edit-user__actions">
                  <Button
                    onClick={closeModal}
                    className="add-reservation__cancel"
                    bsStyle="info"
                    pullRight
                    disabled={loading}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button disabled={loading} bsStyle="info" pullRight fill type="submit">
                    {loading ? (
                      <BeatLoader margin={2.5} size={10} color={"#fff"} />
                    ) : (
                      <span>{service?.id ? "UPDATE" : "ADD"}</span>
                    )}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        }
      />
    </Modal>
  );
};

export default AddService;
